import { t } from '@lingui/macro'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronLeft, ChevronRight, Copy, CopyCheck, Pencil, RefreshCcw, SendHorizontal, XIcon } from 'lucide-react'
import React, { useState } from 'react'
import { Button } from '../../ui/button'
import { TooltipIconButton } from '../TooltipIconButton'

function ContentCopyable({ content, disabled }: { content: string; disabled: boolean }) {
    const [copied, setCopied] = useState(false)

    const handleCopy = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.stopPropagation()
        navigator.clipboard.writeText(content)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    return (
        <TooltipIconButton onClick={(e) => handleCopy(e)} variant='ghost' tooltip={t`Copy content`} disabled={disabled}>
            <AnimatePresence mode='wait' initial={false}>
                {copied ? (
                    <motion.div
                        key='check'
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.15 }}
                    >
                        <CopyCheck style={{ color: '#10b981' }} size={16} />
                    </motion.div>
                ) : (
                    <motion.div
                        key='copy'
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.15 }}
                    >
                        <Copy style={{ color: '#6b7280' }} size={16} />
                    </motion.div>
                )}
            </AnimatePresence>
        </TooltipIconButton>
    )
}

export function BranchSwitcher({
    branch,
    branchOptions,
    onSelect,
    isLoading
}: {
    branch: string | undefined
    branchOptions: string[] | undefined
    onSelect: (branch: string) => void
    isLoading: boolean
}) {
    if (!branchOptions || !branch) return null
    const index = branchOptions.indexOf(branch)

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
            }}
        >
            <Button
                variant='ghost'
                style={{
                    width: '24px',
                    height: '24px',
                    padding: '4px',
                    minWidth: 'auto'
                }}
                onClick={() => {
                    const prevBranch = branchOptions[index - 1]
                    if (!prevBranch) return
                    onSelect(prevBranch)
                }}
                disabled={isLoading}
            >
                <ChevronLeft />
            </Button>
            <span style={{ fontSize: '14px' }}>
                {index + 1} / {branchOptions.length}
            </span>
            <Button
                variant='ghost'
                style={{
                    width: '24px',
                    height: '24px',
                    padding: '4px',
                    minWidth: 'auto'
                }}
                onClick={() => {
                    const nextBranch = branchOptions[index + 1]
                    if (!nextBranch) return
                    onSelect(nextBranch)
                }}
                disabled={isLoading}
            >
                <ChevronRight />
            </Button>
        </div>
    )
}

export function CommandBar({
    content,
    isHumanMessage,
    isAiMessage,
    isEditing,
    setIsEditing,
    handleSubmitEdit,
    handleRegenerate,
    isLoading
}: {
    content: string
    isHumanMessage?: boolean
    isAiMessage?: boolean
    isEditing?: boolean
    setIsEditing?: React.Dispatch<React.SetStateAction<boolean>>
    handleSubmitEdit?: () => void
    handleRegenerate?: () => void
    isLoading: boolean
}) {
    if (isHumanMessage && isAiMessage) {
        throw new Error(t`Can only set one of isHumanMessage or isAiMessage to true, not both.`)
    }

    if (!isHumanMessage && !isAiMessage) {
        throw new Error(t`One of isHumanMessage or isAiMessage must be set to true.`)
    }

    if (isHumanMessage && (isEditing === undefined || setIsEditing === undefined || handleSubmitEdit === undefined)) {
        throw new Error(t`If isHumanMessage is true, all of isEditing, setIsEditing, and handleSubmitEdit must be set.`)
    }

    const showEdit = isHumanMessage && isEditing !== undefined && !!setIsEditing && !!handleSubmitEdit

    if (isHumanMessage && isEditing && !!setIsEditing && !!handleSubmitEdit) {
        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                }}
            >
                <TooltipIconButton
                    disabled={isLoading}
                    tooltip={t`Cancel edit`}
                    variant='ghost'
                    onClick={() => {
                        setIsEditing(false)
                    }}
                >
                    <XIcon />
                </TooltipIconButton>
                <TooltipIconButton disabled={isLoading} tooltip={t`Submit`} variant='secondary' onClick={handleSubmitEdit}>
                    <SendHorizontal />
                </TooltipIconButton>
            </div>
        )
    }

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
            }}
        >
            <ContentCopyable content={content} disabled={isLoading} />
            {isAiMessage && !!handleRegenerate && (
                <TooltipIconButton disabled={isLoading} tooltip={t`Refresh`} variant='ghost' onClick={handleRegenerate}>
                    <RefreshCcw style={{ color: '#6b7280' }} size={16} />
                </TooltipIconButton>
            )}
            {showEdit && (
                <TooltipIconButton
                    disabled={isLoading}
                    tooltip={t`Edit`}
                    variant='ghost'
                    onClick={() => {
                        setIsEditing?.(true)
                    }}
                >
                    <Pencil />
                </TooltipIconButton>
            )}
        </div>
    )
}
